/**
 * gamepad-connection — the pure half of "a controller arrived / a controller left".
 *
 * No React, no `window`, no timers. The store next to this file owns the listeners and the
 * debounce; everything here is a function of its arguments so the interesting cases — a swap, a
 * brown-out, a same-pad replug — are testable as plain values.
 *
 * WHY A THIRD WATCHER EXISTS AT ALL, given two already do. `useGamepadStatus` is window-global but
 * PRESERVES OBJECT IDENTITY when the net state is unchanged, so a same-pad unplug/replug produces
 * no re-render and an announcement derived from it would be silently missed.
 * `DashboardControllerProvider` has the true previous-state ref but is DASHBOARD-SCOPED, so it can
 * never cover Settings, Security or the map — and its disconnect edge is drive-safety code that
 * should not grow a UI dependency. Both are left byte-identical.
 */

import { firstPad } from './gamepad-dom'

export interface GamepadConnectionState {
  connected: boolean
  id: string | null
}

export interface GamepadConnectionEvent {
  kind: 'connected' | 'disconnected'
  /** The pad this is ABOUT. On a disconnect that is the one that went away. */
  id: string | null
  at: number
}

/**
 * How long to wait before announcing, and the reason it is COALESCING rather than trailing.
 *
 * A pad that browns out — a flat battery, a USB re-enumeration — emits disconnect then connect in
 * quick succession. Comparing each raw event against the last ANNOUNCED state means that pair nets
 * to zero and says nothing, which is what an operator wants: their controller did not "leave".
 *
 * 750 ms is long enough to swallow that and short enough that a real unplug still reads as
 * immediate. It delays only the WORDS — the drive stop rides the raw event through the provider's
 * existing `blurFocused()`, with no debounce in front of it.
 */
export const CONNECTION_DEBOUNCE_MS = 750

export function readGamepadState(pads: (Gamepad | null)[]): GamepadConnectionState {
  const pad = firstPad(pads)

  return pad ? { connected: true, id: pad.id } : { connected: false, id: null }
}

/**
 * What to announce, comparing the current state against the last one ANNOUNCED.
 *
 * Against the last announced state, not the last raw one — that is what makes a brown-out silent.
 */
export function diffConnection(
  announced: GamepadConnectionState,
  next: GamepadConnectionState,
  at: number,
): GamepadConnectionEvent | null {
  if (announced.connected === next.connected && announced.id === next.id) return null

  // A pad swapped for another pad is a CONNECT naming the new one. Announcing the departure of a
  // controller the operator has already replaced would describe a state that no longer exists.
  if (next.connected) return { kind: 'connected', id: next.id, at }

  // `next.id` is null here, so the departing pad can only be named from what we last announced.
  return { kind: 'disconnected', id: announced.id, at }
}

/** Longer than this and the name is competing with the message it is meant to qualify. */
const MAX_LABEL_LEN = 40

/**
 * A pad name fit to show a person.
 *
 * Browsers append a vendor tail — "Xbox 360 Controller (STANDARD GAMEPAD Vendor: 045e Product:
 * 028e)" — which is the useful half for identifying hardware and the useless half for a toast.
 * Settings → Controller already shows the raw `gamepad.id` for anyone who needs it.
 *
 * Returns null rather than an empty string when nothing survives, so a caller renders no chip at
 * all instead of an empty one.
 */
export function padLabel(id: string | null | undefined): string | null {
  if (!id) return null

  const stripped = id
    .replace(/\s*\([^)]*\)\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim()

  if (stripped.length === 0) return null

  return stripped.length > MAX_LABEL_LEN ? stripped.slice(0, MAX_LABEL_LEN).trimEnd() : stripped
}
